import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import { TbAlertSquareRoundedFilled } from "react-icons/tb";
import {
    FieldTypedReplaceableValue,
    FormField as ApiFormField, FormFieldDescriptor, FormFieldType,
    FormFieldVisibility, Ticket, TicketInput, useGetFormsQuery, useGetConditionalFormQuery, Form as ApiForm, CustomField,
} from "../../__generated__/api";
import EditableControl, { ControlType } from "../editable-text/EditableText";
import FormField from "../form-field/FormField";
import { HtmlRenderer } from "../html-render/HtmlRenderer";
import { LoadingIcon } from "../loading/Loading";
// import Styles from "./Form.module.scss";

export enum FormDisplayUserType {
    AGENT,
    REQUESTOR
}

export enum FormCreateType {
    CREATABLE = "CREATABLE",
    READONLY = "READONLY"
}
interface FormProps {
    className?: string;
    descriptionMessage?: ReactElement | string;
    onChange?: (ticket: Partial<TicketInput>) => void;
    onChangeRequestType?: (old: ApiForm | undefined, newForm: ApiForm) => void;
    ticket?: Ticket | undefined;
    isEditable: boolean;
    canSelectRequestType?: boolean;
    shouldValidateAutomations?: boolean;
    selectDefaultRequestType?: boolean;
    displayUserType?: FormDisplayUserType;
    isEditing?: boolean;
    shouldShowDescriptions?: boolean;
    shouldApplyRequired?: boolean;
    shouldValidateAutomation?: boolean;
    isFormField?: boolean;
    formId?: string;
    setCreateType?: (type: FormCreateType) => void;
    onEmptyMessage?: ReactElement | string;
    skipFields?: string[];
    shouldFilterAgentRequired?: boolean;
}

export const SUBJECT_ID = "urn:fq:form-field:subject";
export const DESCRIPTION_ID = "urn:fq:form-field:description";
export const PRIORITY_ID = "urn:fq:form-field:priority";
export const REQUESTOR_ID = "urn:fq:form-field:requestorId";
export const TAGS_ID = "urn:fq:form-field:tags";

const HANDLED_TYPES = new Set([SUBJECT_ID, DESCRIPTION_ID]);
const DEFAULT_FIELDS: ApiFormField[] = [
    {
        id: SUBJECT_ID,
        name: "Subject",
        description: "The ticket subject",
        isSystemField: true,
        type: FormFieldType.Text,
        visibility: FormFieldVisibility.Reusable,
    },
    {
        id: DESCRIPTION_ID,
        name: "Description",
        description: "The ticket description",
        isSystemField: true,
        type: FormFieldType.LongText,
        visibility: FormFieldVisibility.Reusable,
    },
];

export default function Form({
    isEditable, onChange, ticket, className, canSelectRequestType, displayUserType, isEditing,
    setCreateType, selectDefaultRequestType, shouldShowDescriptions, shouldApplyRequired, isFormField,
    formId: inputFormId, shouldValidateAutomations, onChangeRequestType, onEmptyMessage, skipFields,
    shouldValidateAutomation, shouldFilterAgentRequired, descriptionMessage,
}: FormProps): ReactElement | null {
    // eslint-disable-next-line prefer-const
    let { data: formData, loading: isLoading } = useGetFormsQuery({
        variables: {
            needforms: inputFormId == null || canSelectRequestType === true,
        },
    });

    const { data: form, loading: isFormLoading, refetch: refetchForm } = useGetConditionalFormQuery({
        skip: inputFormId == null || !shouldValidateAutomation,
        variables: {
            formId: inputFormId,
            shouldValidateAutomations: shouldValidateAutomations ?? false,
        },
    });

    const [formId, setFormId] = useState<string | undefined>();

    useEffect(() => {
        if (ticket?.formId) {
            setFormId(ticket?.formId);
        }
    }, [ticket?.formId]);

    useEffect(() => {
        if (inputFormId) {
            setFormId(inputFormId);
        }
    }, [inputFormId]);

    const skipped = useMemo(() => new Set(skipFields ?? []), skipFields);

    const {
        currentForm, fieldOverrides, formFields, formFieldMap,
    } = useMemo(() => {
        const textField: keyof FormFieldDescriptor = displayUserType === FormDisplayUserType.AGENT
            ? "agentText"
            : "requestorText";

        const allFields = formData?.formFields.toIdMap("id") ?? {};
        const currForm = form?.form ?? formData?.forms?.find(current => current.id === formId);
        const overrides = currForm?.fields?.filterNull().toIdMap(row => row?.fieldId ?? "") ?? {};
        let fields = currForm?.fields?.filterNull().mapFilter(field => (
            field[textField] ? { text: field[textField] } : allFields[field.fieldId?.toString()]))
            ?? DEFAULT_FIELDS;
        const map = ticket?.customFields?.toIdMap("formFieldId", value => ({
            formFieldId: value.formFieldId,
            displayValue: value.displayValue,
            formField: value.formField,
            value: value.value ? value.value : {
                ...value,
            },
            type: value.type,
        } as CustomField)) ?? {};
        map[SUBJECT_ID] = {
            formFieldId: SUBJECT_ID,
            value: {
                formFieldId: SUBJECT_ID,
                string: ticket?.subject,
            },
        };
        map[DESCRIPTION_ID] = {
            formFieldId: DESCRIPTION_ID,
            value: {
                formFieldId: DESCRIPTION_ID,
                string: ticket?.description,
            },
        };
        map[PRIORITY_ID] = {
            formFieldId: PRIORITY_ID,
            value: {
                formFieldId: PRIORITY_ID,
                string: ticket?.priority,
            },
        };
        map[REQUESTOR_ID] = {
            formFieldId: REQUESTOR_ID,
            value: {
                formFieldId: REQUESTOR_ID,
                string: ticket?.requestorId,
            },
        };
        map[TAGS_ID] = {
            formFieldId: TAGS_ID,
            value: {
                formFieldId: TAGS_ID,
                options: ticket?.tags,
            },
        };

        if (shouldFilterAgentRequired) {
            fields = fields.filter(field => {
                const fieldId = (field as ApiFormField).id;
                return overrides[fieldId]?.isRequiredForAgent && map[fieldId]?.value == null;
            });
        }

        return {
            textField,
            allFields,
            currentForm: currForm,
            fieldOverrides: overrides,
            formFields: fields,
            formFieldMap: map,
        };
    }, [formData, form, formId, ticket]);

    useEffect(() => {
        const canCreate = formFields.some(field => (field as {text: string}).text == null);
        setCreateType?.(canCreate ? FormCreateType.CREATABLE : FormCreateType.READONLY);

        // eslint-disable-next-line eqeqeq
        if (formId != ticket?.formId) {
            onChange?.({
                formId,
            });
        }
    }, [formId]);

    const changeRequestType = (newId: string) => {
        const oldCurrent = currentForm;
        if (currentForm?.id === newId) {
            return;
        }
        setFormId(newId);
        if (onChangeRequestType) {
            (async () => {
                const { data } = await refetchForm({
                    formId: newId,
                    shouldValidateAutomations: shouldValidateAutomations ?? false,
                });
                onChangeRequestType(oldCurrent as ApiForm, data.form as ApiForm);
            })();
        }
    };

    useEffect(() => {
        if (selectDefaultRequestType && !formId && formData?.forms?.isNotEmpty) {
            const firstForm = formData?.forms?.firstElement;
            changeRequestType(firstForm.id);
        }
    }, [formData?.forms]);

    if ((selectDefaultRequestType && isLoading) || isFormLoading) {
        return (
            <div className="d-flex justify-content-center">
                <LoadingIcon />
            </div>
        );
    }

    if (!canSelectRequestType && formFields.isEmpty && !onEmptyMessage) {
        return null;
    }
    return (
        <div className={className}>
            {descriptionMessage}
            {(canSelectRequestType && (formData?.forms?.isNotEmpty || currentForm)) && (
                <div className="mb-3">
                    <small className="text-gray-500">
                        Request Type
                    </small>
                    <div>
                        {/* <RequestTypes
                            onSelected={value => setFormId(value)}
                            value={formId}
                        /> */}
                        <EditableControl
                            isReadOnly={!isEditable}
                            type={ControlType.SELECT}
                            value={formId}
                            isEditing={isEditing}
                            isRequired
                            shouldFocus
                            placeholder="Request Type"
                            onChange={changed => changeRequestType(changed as string)}
                            options={formData?.forms?.map(opt => ({
                                label: opt.name,
                                value: opt.id,
                                data: opt,
                            }))}
                        >
                            {currentForm ? <span>{currentForm.name}</span> : undefined}
                        </EditableControl>
                    </div>

                </div>
            )}

            {formFields.isEmpty && onEmptyMessage}

            {formFields.filter(field => !skipped.has((field as {id: string}).id)).map(rawFormField => {
                const withText = rawFormField as {text: string};
                if (withText.text) {
                    return (
                        <div className="mb-3 alert-light p-3 rounded" key={withText.text}>
                            <HtmlRenderer html={withText.text} />
                        </div>
                    );
                }
                const formField = rawFormField as ApiFormField;
                const valueField = formFieldMap[formField.id];
                const override = fieldOverrides?.[formField.id];
                let isRequired = override?.isRequired ?? formField.isRequired ?? false;
                if (shouldFilterAgentRequired) {
                    isRequired = override?.isRequiredForAgent ?? isRequired;
                }
                return (
                    <div className="mb-3" key={formField.id}>
                        <small className="text-gray-500" title={override?.description ?? formField?.description as string}>
                            {override?.name ?? formField?.name}
                            {" "}
                            {shouldApplyRequired && !isRequired && <span>(Optional)</span>}
                        </small>
                        <div>
                            {formField.id === DESCRIPTION_ID && (
                                <EditableControl
                                    isReadOnly={!isEditable}
                                    isRequired={isRequired}
                                    isFormField={isFormField}
                                    shouldFocus={false}
                                    value={ticket?.description?.trim()
                                        ?? formField.defaultValue?.string
                                        ?? override?.defaultValue?.value.string as string | undefined}
                                    placeholder={override?.placeholder
                                            ?? formField?.placeholder
                                             ?? "Enter Description"}
                                    type={ControlType.LONG_TEXT}
                                    isEditing={isEditing}
                                    onChange={changed => onChange?.({ description: changed as string })}
                                />
                            )}
                            {formField.id === SUBJECT_ID && (
                                <EditableControl
                                    isReadOnly={!isEditable}
                                    isRequired={isRequired}
                                    isFormField={isFormField}
                                    shouldFocus={false}
                                    value={ticket?.subject?.trim()
                                            ?? formField.defaultValue?.string
                                            ?? override?.defaultValue?.value.string as string | undefined}
                                    placeholder={override?.placeholder
                                            ?? formField?.placeholder
                                             ?? "Enter Subject"}
                                    isEditing={isEditing}
                                    onChange={changed => onChange?.({ subject: changed as string })}
                                />
                            )}
                            {!HANDLED_TYPES.has(formField.id) && (
                                <FormField
                                    isEditable={isEditable}
                                    isRequired={isRequired}
                                    isFormField={isFormField}
                                    shouldFocus={false}
                                    formField={{
                                        ...formField,
                                        placeholder: override?.placeholder ?? formField?.placeholder,
                                    }}
                                    onChange={onChange}
                                    isEditing={isEditing}
                                    value={valueField}
                                    defaultValue={override?.defaultValue as FieldTypedReplaceableValue}
                                />
                            )}

                        </div>
                        {shouldShowDescriptions && (
                            <small>
                                {formField.description}
                            </small>
                        )}

                    </div>

                );
            })}
        </div>
    );
}

Form.defaultProps = {
    onChange: () => {
        // nothing
    },
    ticket: undefined,
    canSelectRequestType: false,
    selectDefaultRequestType: false,
    className: "",
    isEditing: undefined,
    displayUserType: FormDisplayUserType.AGENT,
    setCreateType: undefined,
    shouldShowDescriptions: false,
    shouldApplyRequired: false,
    isFormField: undefined,
    formId: undefined,
    shouldValidateAutomations: false,
    onEmptyMessage: "",
    skipFields: [],
    shouldValidateAutomation: false,
    shouldFilterAgentRequired: false,
    descriptionMessage: undefined,
    onChangeRequestType: () => {
        // do nothing
    },
};
